import { useRef } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import Lottie, { type LottieRefCurrentProps } from 'lottie-react';
import iconAnim from '@/assets/logo-icon.json';
import lockupAnim from '@/assets/logo-lockup.json';

// Animated VisionHeight logo — the exact Lottie assets from visionheight.com
// (VIS-390). Colors are baked in (icon = brand blue; lockup = navy + blue), so
// there's no `currentColor` tint anymore. Plays once on mount, then replays on
// hover (like the site).
const ICON_AR = 122 / 85; // logo-icon.json canvas aspect ratio
const LOCKUP_AR = 644 / 104; // logo-lockup.json canvas aspect ratio

/** The animated VisionHeight icon mark. `size` = rendered height in px. */
export function VisionHeightLogo({ size = 37 }: { size?: number }) {
  const ref = useRef<LottieRefCurrentProps>(null);
  // lottie-web renders the <svg> as `display:inline`, so it sits on the text
  // baseline and picks up a ~descender gap that pushes it a few px low next to a
  // wordmark. Force the svg to block so its box hugs the mark and centers cleanly.
  return (
    <Box sx={{ display: 'flex', lineHeight: 0, '& svg': { display: 'block' } }}>
      <Lottie
        lottieRef={ref}
        animationData={iconAnim}
        loop={false}
        onMouseEnter={() => ref.current?.goToAndPlay(0)}
        style={{ height: size, width: size * ICON_AR }}
        aria-hidden
      />
    </Box>
  );
}

/** The animated VisionHeight lockup (icon + wordmark). `height` in px. */
export function VisionHeightLockup({ height = 28 }: { height?: number }) {
  const ref = useRef<LottieRefCurrentProps>(null);
  return (
    <Lottie
      lottieRef={ref}
      animationData={lockupAnim}
      loop={false}
      onMouseEnter={() => ref.current?.goToAndPlay(0)}
      style={{ height, width: height * LOCKUP_AR, display: 'block' }}
      aria-label="VisionHeight"
    />
  );
}

/**
 * Logo mark + "VisionHeight" wordmark. `vertical` (default) is the centered home
 * lockup; `horizontal` is the compact inline lockup (e.g. login header).
 */
export function BrandMark({
  logoSize = 44,
  orientation = 'vertical',
}: {
  logoSize?: number;
  orientation?: 'vertical' | 'horizontal';
}) {
  const horizontal = orientation === 'horizontal';
  return (
    <Stack
      direction={horizontal ? 'row' : 'column'}
      alignItems="center"
      spacing={horizontal ? 1 : 2}
    >
      <Box sx={{ color: 'primary.main' }}>
        <VisionHeightLogo size={logoSize} />
      </Box>
      {/* Figma DS wordmark: two-tone "Vision" (navy) + "Height" (blue), bold. */}
      <Typography
        component="h1"
        sx={{
          fontWeight: 700,
          fontSize: horizontal ? '1.5rem' : { xs: '2rem', sm: '2.5rem' },
          letterSpacing: '-0.01em',
          color: 'text.primary',
          lineHeight: 1.1,
        }}
      >
        Vision<Box component="span" sx={{ color: 'primary.main' }}>Height</Box>
      </Typography>
    </Stack>
  );
}
